/**
 * World Engine store — the single client-side source of truth for the
 * living laboratory. The 3D world, HTML overlays, behavioral engine and the
 * AI interface all read and write here. Server components never import this.
 */

import { create } from 'zustand';
import type {
  ExperienceMode,
  Freshness,
  FreshnessReason,
  IntentId,
  LabAgent,
  LabEvent,
  LabHint,
  LabStatus,
  LiveMode,
  VisitorState,
  ZoneId,
} from './types';
import { fallbackAgents } from '../content/agents.ts';
import { rostersEqual } from './roster-diff.ts';

export interface FocusTarget {
  type: 'project' | 'research' | 'node' | 'agent';
  id: string;
}

interface WorldState {
  // ── Experience ─────────────────────────────────────────────
  mode: ExperienceMode;
  reducedMotion: boolean;
  webglFailed: boolean;
  /** True once the intro sequence has completed (or was skipped). */
  booted: boolean;
  audioOn: boolean;
  setMode: (m: ExperienceMode) => void;
  setReducedMotion: (v: boolean) => void;
  setWebglFailed: (v: boolean) => void;
  setBooted: (v: boolean) => void;
  setAudio: (v: boolean) => void;

  // ── World ──────────────────────────────────────────────────
  zone: ZoneId;
  labStatus: LabStatus;
  focus: FocusTarget | null;
  compare: { a: string; b: string } | null;
  setZone: (z: ZoneId) => void;
  setLabStatus: (s: LabStatus) => void;
  setFocus: (f: FocusTarget | null) => void;
  setCompare: (c: { a: string; b: string } | null) => void;

  // ── Live layer ─────────────────────────────────────────────
  // `agents` starts as the static fallback registry (deterministic for
  // SSR/first paint) and is only ever replaced by `setAgents`, once a
  // roster frame from the Genesis-Live bridge has been validated
  // (LiveActivity.tsx). `freshness`/`runId`/`epoch`/`rosterObservedAt`
  // mirror the bridge's `status` frames; `rosterLive` flips true the
  // first time `setAgents` actually replaces the fallback and never flips
  // back — it says whether what's in `agents` is live-sourced at all,
  // independent of whether the connection is currently LIVE/STALE/OFFLINE.
  events: LabEvent[];
  agents: LabAgent[];
  rosterLive: boolean;
  rosterObservedAt: string | null;
  freshness: Freshness;
  /** Why the freshness is not LIVE, when the bridge said so. */
  freshnessReason: FreshnessReason | undefined;
  /**
   * Which universe the freshness above describes: `live` = the flagship
   * Genesis-Live run, `canary` = an engineering canary whose decisions may
   * be the engine's code fallback, `null` = not known / nothing observed.
   * Named `liveMode` because `mode` is already the Experience rendering
   * tier. Any surface that says "real agents" must read this too — a canary
   * is observer-backed, but it is not the population the site talks about.
   */
  liveMode: LiveMode | null;
  /**
   * Population counters from the bridge's `status` frame: `activeAgents` is
   * the size of the observed universe, `rosterShown` the number of rows it
   * actually sent. Any surface that states a population size reads
   * `activeAgents`; `agents.length` only ever describes the rows on hand.
   * Null until a frame carrying counters arrives.
   */
  liveStats: { activeAgents: number; rosterShown: number } | null;
  /**
   * A frame the browser refused (a roster that failed `validateRoster`, a
   * malformed `status`). Swallowing this silently left the page reading
   * OFFLINE forever with no way for a visitor to tell a rejected frame from
   * a real outage; it is surfaced instead. Cleared by the next good frame.
   */
  bridgeFault: string | null;
  setBridgeFault: (fault: string | null) => void;
  runId: string | null;
  epoch: number | null;
  pushEvent: (e: LabEvent) => void;
  updateAgent: (id: string, patch: Partial<LabAgent>) => void;
  /** Replace the roster. No-ops (no re-render, no new array) when the
   * incoming roster is identical in content to the current one, and when it
   * is empty — an empty live roster must never wipe the field to nothing
   * (and must never latch `rosterLive`), since the site then has nothing
   * observer-backed to show at all. */
  setAgents: (agents: LabAgent[]) => void;
  /** Apply a validated `status` SSE frame. */
  setLiveStatus: (status: {
    freshness: Freshness;
    reason?: FreshnessReason;
    runId: string | null;
    epoch: number | null;
    observedAt: string | null;
    mode?: LiveMode | null;
    stats?: { activeAgents: number; rosterShown: number } | null;
  }) => void;

  // ── UI ─────────────────────────────────────────────────────
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  hint: LabHint | null;
  dismissedHints: string[];
  setHint: (h: LabHint | null) => void;
  dismissHint: (id: string) => void;

  // ── Visitor (session-local, anonymous) ─────────────────────
  visitor: VisitorState;
  recordZone: (z: ZoneId) => void;
  recordProject: (slug: string) => void;
  recordResearch: (slug: string) => void;
  recordAgentInteraction: () => void;
  recordAsk: () => void;
  setIdle: (ms: number) => void;
  setIntent: (intent: IntentId, confidence: Partial<Record<IntentId, number>>) => void;
}

const initialVisitor = (): VisitorState => ({
  sessionStart: typeof performance !== 'undefined' ? performance.timeOrigin : 0,
  visitedZones: [],
  viewedProjects: [],
  viewedResearch: [],
  agentInteractions: 0,
  askLabUses: 0,
  idleMs: 0,
  intent: 'unknown',
  intentConfidence: {},
});

const MAX_EVENTS = 50;

export const useWorld = create<WorldState>((set) => ({
  mode: 'balanced',
  reducedMotion: false,
  webglFailed: false,
  booted: false,
  audioOn: false,
  setMode: (mode) => set({ mode }),
  setReducedMotion: (reducedMotion) => set({ reducedMotion }),
  setWebglFailed: (webglFailed) => set({ webglFailed }),
  setBooted: (booted) => set({ booted }),
  setAudio: (audioOn) => set({ audioOn }),

  zone: 'core',
  labStatus: 'active',
  focus: null,
  compare: null,
  setZone: (zone) => set({ zone }),
  setLabStatus: (labStatus) => set({ labStatus }),
  setFocus: (focus) => set({ focus }),
  setCompare: (compare) => set({ compare }),

  events: [],
  agents: fallbackAgents.map((a) => ({ ...a })),
  rosterLive: false,
  rosterObservedAt: null,
  freshness: 'SYNCING',
  freshnessReason: undefined,
  liveMode: null,
  liveStats: null,
  bridgeFault: null,
  setBridgeFault: (bridgeFault) => set({ bridgeFault }),
  runId: null,
  epoch: null,
  pushEvent: (e) =>
    set((s) => ({ events: [e, ...s.events].slice(0, MAX_EVENTS) })),
  updateAgent: (id, patch) =>
    set((s) => ({
      agents: s.agents.map((a) => (a.id === id ? { ...a, ...patch } : a)),
    })),
  setAgents: (agents) =>
    set((s) =>
      agents.length === 0 || rostersEqual(s.agents, agents) ? {} : { agents, rosterLive: true },
    ),
  setLiveStatus: ({ freshness, reason, runId, epoch, observedAt, mode, stats }) =>
    set({
      freshness,
      freshnessReason: reason,
      runId,
      epoch,
      rosterObservedAt: observedAt,
      liveMode: mode ?? null,
      liveStats: stats ?? null,
      bridgeFault: null,
    }),

  paletteOpen: false,
  setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
  hint: null,
  dismissedHints: [],
  setHint: (hint) => set({ hint }),
  dismissHint: (id) =>
    set((s) => ({
      hint: s.hint?.id === id ? null : s.hint,
      dismissedHints: [...s.dismissedHints, id],
    })),

  visitor: initialVisitor(),
  recordZone: (z) =>
    set((s) => ({
      visitor: {
        ...s.visitor,
        visitedZones: s.visitor.visitedZones.includes(z)
          ? s.visitor.visitedZones
          : [...s.visitor.visitedZones, z],
      },
    })),
  recordProject: (slug) =>
    set((s) => ({
      visitor: {
        ...s.visitor,
        viewedProjects: s.visitor.viewedProjects.includes(slug)
          ? s.visitor.viewedProjects
          : [...s.visitor.viewedProjects, slug],
      },
    })),
  recordResearch: (slug) =>
    set((s) => ({
      visitor: {
        ...s.visitor,
        viewedResearch: s.visitor.viewedResearch.includes(slug)
          ? s.visitor.viewedResearch
          : [...s.visitor.viewedResearch, slug],
      },
    })),
  recordAgentInteraction: () =>
    set((s) => ({
      visitor: { ...s.visitor, agentInteractions: s.visitor.agentInteractions + 1 },
    })),
  recordAsk: () =>
    set((s) => ({
      visitor: { ...s.visitor, askLabUses: s.visitor.askLabUses + 1 },
    })),
  setIdle: (ms) => set((s) => ({ visitor: { ...s.visitor, idleMs: ms } })),
  setIntent: (intent, intentConfidence) =>
    set((s) => ({ visitor: { ...s.visitor, intent, intentConfidence } })),
}));

/** Map a pathname to the zone it belongs to (locale prefix ignored). */
export function zoneForPath(pathname: string): ZoneId {
  const p = pathname.replace(/^\/(en|ru)(?=\/|$)/, '') || '/';
  if (p.startsWith('/research')) return 'research';
  if (p.startsWith('/projects')) return 'projects';
  if (p.startsWith('/agents')) return 'agents';
  if (p.startsWith('/knowledge')) return 'knowledge';
  if (p.startsWith('/observatory')) return 'observatory';
  if (p.startsWith('/publications')) return 'archive';
  if (p.startsWith('/contact') || p.startsWith('/about')) return 'gateway';
  return 'core';
}
